import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchLocationUsers,
  fetchAvailableTeamMembers,
  createLocationUser,
  updateLocationUser,
  deleteLocationUser,
} from '@/lib/api/location-users'
import type { LocationUserInsert, LocationUserUpdate } from '@/types/location-users'
import { toast } from 'sonner'

/**
 * Query key factory for location users
 * Provides consistent keys for cache management
 */
export const locationUserKeys = {
  all: ['locationUsers'] as const,
  lists: () => [...locationUserKeys.all, 'list'] as const,
  list: (locationId: string) => [...locationUserKeys.lists(), locationId] as const,
  details: () => [...locationUserKeys.all, 'detail'] as const,
  detail: (id: string) => [...locationUserKeys.details(), id] as const,
  available: (locationId: string, organizationId: string) =>
    [...locationUserKeys.all, 'available', locationId, organizationId] as const,
}

/**
 * Fetch all users assigned to a location
 */
export function useLocationUsers(locationId: string | undefined) {
  return useQuery({
    queryKey: locationUserKeys.list(locationId || ''),
    queryFn: () => fetchLocationUsers(locationId!),
    enabled: !!locationId,
  })
}

/**
 * Fetch available team members not yet assigned to location
 */
export function useAvailableTeamMembers(locationId: string | undefined, organizationId: string | undefined) {
  return useQuery({
    queryKey: locationUserKeys.available(locationId || '', organizationId || ''),
    queryFn: () => fetchAvailableTeamMembers(locationId!, organizationId!),
    enabled: !!locationId && !!organizationId,
  })
}

/**
 * Assign a user to a location
 */
export function useCreateLocationUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: LocationUserInsert) => createLocationUser(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: locationUserKeys.list(variables.location_id),
      })
      queryClient.invalidateQueries({
        queryKey: locationUserKeys.available(variables.location_id, ''),
        exact: false,
      })
      toast.success('User assigned to location')
    },
    onError: (error: Error) => {
      toast.error('Failed to assign user', {
        description: error.message,
      })
    },
  })
}

/**
 * Update a location user's role
 */
export function useUpdateLocationUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LocationUserUpdate }) =>
      updateLocationUser(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: locationUserKeys.list(data.location_id),
      })
      queryClient.invalidateQueries({
        queryKey: locationUserKeys.detail(data.id),
      })
      toast.success('Location user role updated')
    },
    onError: (error: Error) => {
      toast.error('Failed to update role', {
        description: error.message,
      })
    },
  })
}

/**
 * Remove a user from a location
 */
export function useDeleteLocationUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteLocationUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: locationUserKeys.lists(),
      })
      toast.success('User removed from location')
    },
    onError: (error: Error) => {
      toast.error('Failed to remove user', {
        description: error.message,
      })
    },
  })
}
